import React, { useState, useEffect } from "react";
import { Menu } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useTenantTheme } from "../../contexts/TenantThemeContext";
import routes from "../../components/Routes/Routes";
import styles from "./Sidebar.module.scss";
import logoHorizontal from "../../assets/images/logo-horizontal.svg";

const API_BASE_URL = process.env.REACT_APP_BASE_URL || "http://localhost:4000";

function resolveLogoUrl(logoUrl?: string, logoFilePath?: string): string | null {
  const src = logoUrl || logoFilePath;
  if (!src || typeof src !== "string" || !src.trim()) return null;
  const trimmed = src.trim();
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
  const path = trimmed.startsWith("/") ? trimmed.slice(1) : trimmed;
  const relPath = path.startsWith("uploads/") ? path.replace(/^uploads\//, "") : path;
  return `${API_BASE_URL}/api/uploads/${relPath}`;
}

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasRole, hasPermission, hasSubscriptionModule, hasSubscriptionRoute, hasRouteAccess, isPermissionsLoading } = useAuth();
  const { tenantConfig } = useTenantTheme() ?? {};
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  // Update selected keys based on current location
  useEffect(() => {
    const currentPath = location.pathname;

    const findSelectedKey = (routes: any[]): string[] => {
      for (const route of routes) {
        // Check if current path matches this route exactly
        if (route.path === currentPath) {
          return [route.children ? route.key : route.path];
        }

        // Check children
        if (route.children) {
          const childResult = findSelectedKey(route.children);
          if (childResult.length > 0) {
            // Add parent key to openKeys if child is selected
            if (route.key) {
              setOpenKeys(prev => Array.from(new Set([...prev, route.key])));
            }
            return childResult;
          }
        }
      }
      return [];
    };

    const selectedKey = findSelectedKey(routes);
    setSelectedKeys(selectedKey);
  }, [location.pathname]);

  const renderMenuItems = (menuRoutes: any) => {
    return menuRoutes
      .filter((route: any) => {
        // Filter out hidden routes
        if (route.hidden) return false;

        // Check required roles (e.g. SUPER_ADMIN only)
        if (route.requiredRoles && route.requiredRoles.length > 0) {
          if (!hasRole(route.requiredRoles)) {
            return false;
          }
        }

        // Check route-based access (subscription plan routes) - most specific check
        if (route.key && !hasSubscriptionRoute(route.key)) {
          return false;
        }

        // Check module-based access (subscription plan modules)
        if (route.module) {
          if (!hasSubscriptionModule([route.module])) {
            return false;
          }
        }

        // Check dynamic route permissions mapping
        if (route.key && !hasRouteAccess(route.key)) {
          return false;
        }

        return true;
      })
      .map((route: any) => {
        const children = route.children ? renderMenuItems(route.children) : undefined;
        return {
          key: route.children ? route.key : route.path,
          label: route.title,
          icon: route.icon,
          children,
          onClick: route.children ? undefined : () => navigate(route.path)
        };
      })
      .filter((item: any) => !item.children || item.children.length > 0);
  };

  const handleOpenChange = (keys: string[]) => {
    setOpenKeys(keys);
  };

  const logoUrl = resolveLogoUrl(tenantConfig?.logo_url, tenantConfig?.logo_file_path);
  const logoSrc = logoUrl || logoHorizontal;

  return (
    <div className={styles.sidebarContainer}>
      <div className={styles.logo}>
        <img src={logoSrc} alt={tenantConfig?.company_name || "VetanaHR"} className={styles.logoImage} />
      </div>
      <Menu
        mode="inline"
        theme="dark"
        selectedKeys={selectedKeys}
        openKeys={openKeys}
        onOpenChange={handleOpenChange}
        className={styles.sidebarMenu}
        style={{ borderRight: 0 }}
        items={isPermissionsLoading ? [] : renderMenuItems(routes)}
      />
    </div>
  );
};

export default Sidebar;
